import { useRef, useState } from 'react';
import { ImageIcon, RefreshCw, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { BrandingKind, removeBrandingImage, uploadBrandingImage } from '@/lib/brandingApi';

// Preview frame per kind: a square logo, a wide letterhead, a short signature strip
const FRAME: Record<BrandingKind, string> = {
  logo: 'w-24 h-24',
  letterhead: 'w-full max-w-md h-24',
  signature: 'w-56 h-20',
};

interface Props {
  kind: BrandingKind;
  label: string;
  description: string;
  /** Current image as a data URL, null when none */
  image: string | null;
  /** After an upload or removal: reload the images */
  onChanged: () => void;
}

/** One branding image in Settings: preview, upload or replace, remove */
export default function BrandingImageField({ kind, label, description, image, onChanged }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = async (file: File | undefined) => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await uploadBrandingImage(kind, file);
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Upload failed');
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const remove = async () => {
    if (!window.confirm(`Remove the ${label.toLowerCase()}?`)) return;
    setBusy(true);
    setError(null);
    try {
      await removeBrandingImage(kind);
      onChanged();
    } catch (e: any) {
      setError(e?.message || 'Could not remove');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-2">
      <div>
        <p className="text-sm font-medium">{label}</p>
        <p className="text-xs text-muted-foreground">{description}</p>
      </div>
      {/* White like the printed page, so the image looks as it will on paper in both themes */}
      <div className={cn('flex items-center justify-center rounded-md border border-border bg-white p-2 overflow-hidden', FRAME[kind])}>
        {image
          ? <img src={image} alt={label} className="max-w-full max-h-full object-contain" />
          : <span className="flex flex-col items-center gap-1 text-xs text-[#6b7280]"><ImageIcon className="w-5 h-5" aria-hidden />None yet</span>}
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
          onChange={e => pick(e.target.files?.[0])} />
        <Button type="button" size="sm" variant="outline" disabled={busy} onClick={() => inputRef.current?.click()} className="gap-1.5">
          {busy ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
          {image ? 'Replace' : 'Upload'}
        </Button>
        {image && (
          <Button type="button" size="sm" variant="ghost" disabled={busy} onClick={remove} className="gap-1.5 text-muted-foreground">
            <Trash2 className="w-3.5 h-3.5" />
            Remove
          </Button>
        )}
      </div>
      {error && <p role="alert" className="text-xs text-destructive">{error}</p>}
    </div>
  );
}
